<script setup lang="ts">import { ref as _ref, computed as _computed } from 'vue';

import { watch } from 'vue';
import { AssetService, LineageEntityDto } from '@/api';
import type {
  AuthorizedAssetHeaderDto,
  DagAssetOverview,
  DashboardAssetOverview,
  DatasetAssetOverview,
  UnauthorizedAssetHeaderDto,
  DeclaredAssetOverview,
  TransformationAssetOverview,
} from '@/api';
import { getProviderIconSrc } from '@/utils/provider-icons';
import AccessDenied from '@/components/panels/Access-Denied.vue';
import AssetOverviewDetail from '@/components/asset/overview/Asset-Overview-Detail.vue';
import AssetOverviewTransformationPanel from '@/components/asset/overview/Asset-Overview-Transformation-Panel.vue';
import OrchestrationStatus from '@/components/asset/overview/Orchestration-Status.vue';
import TransformationStatus from '@/components/asset/overview/Transformation-Status.vue';

interface DatasetPanelProps {
  urn: string;
}
const props =  defineProps({
  urn: null
});

let headerInfo: AuthorizedAssetHeaderDto | UnauthorizedAssetHeaderDto | null = _ref(null);
let overviewData: DagAssetOverview | DashboardAssetOverview | DatasetAssetOverview | DeclaredAssetOverview | TransformationAssetOverview | null = _ref(null);
let assetData = _ref<AuthorizedAssetHeaderDto | UnauthorizedAssetHeaderDto | null>(null);
let isAuthorized: null | undefined | boolean = _ref(null);

const associatedDomainsNames = _computed(() => (headerInfo.value ? (headerInfo.value as UnauthorizedAssetHeaderDto).associatedDomainsNames : []));

const qualityStatus = _computed(() => headerInfo.value?.qualityStatus);
const dagRun = _computed(() => headerInfo.value?.dagRunDto);
const dbtModel = _computed(() => headerInfo.value?.dbtModelDto);
const image = _computed(() => getProviderIconSrc(headerInfo.value?.lineagePlatform));
const uri = _computed(() => {
  if (headerInfo.value && 'uri' in headerInfo.value) return headerInfo.value.uri;

  return '';
});

const getAssetOverview = async () => {
  headerInfo.value = await AssetService.getAssetHeaderByUrn({ urn: props.urn });
  isAuthorized.value = headerInfo.value.accessAuthorized;
  if (!isAuthorized.value) return;
  overviewData.value = await AssetService.getAssetOverviewByUrn({ urn: props.urn });
};

const getAsset = async () => {
  assetData.value = await AssetService.getAssetHeaderByUrn({ urn: props.urn });
};

const updateHandler = () => {
  getAssetOverview();
};

const detailComponent = _computed(() => {
  if (!headerInfo.value) return null;

  if (headerInfo.value.entityType === LineageEntityDto.entityType.TRANSFORMATION) {
    return AssetOverviewTransformationPanel;
  }

  return AssetOverviewDetail;
});
const typeLabel = _computed(() => headerInfo.value?.displayedTypeLabel || headerInfo.value?.displayedType);

const qualifiedNamePrefix = _computed(() => headerInfo.value?.qualifiedNamePrefix);

const label = _computed(() => (qualifiedNamePrefix.value === undefined ? typeLabel.value : `${typeLabel.value} - ${qualifiedNamePrefix.value}`));

watch(() => props.urn, () => {
  getAssetOverview();
  getAsset();
}, { immediate: true });
</script>

<template lang="pug">
v-card.pr-2( class="d-flex flex-column flex-grow-1" )

  .d-inline-flex( class="pa-4 mb-n8" style="margin-right: 30px;")

    v-avatar(tile size="35" class="flex-grow-0 flex-shrink-0 mr-2")
      img( :src="image" :alt="headerInfo?.lineagePlatformLabel" )

    .d-flex.flex-column.align-start
      span.overline.grey--text.d-block {{ label }}
      h6.text-h6.text-break.font-weight-medium {{ headerInfo?.name }}
      TransformationStatus( v-if="dbtModel" :lastRunDate="dbtModel.lastRunDate" :lastRunStatus="dbtModel.lastRunStatus" )
      OrchestrationStatus( v-if="dagRun" :lastExecutionDate="dagRun.executionDate" :lastRunState="dagRun.state" )
      QualityStatus.mt-1( :value="qualityStatus" )

  component(
    v-if="isAuthorized"
    :is="detailComponent"
    isSmall
    :overview-data="overviewData"
    :asset-data="assetData"
    :urn="urn"
    :sourceUri="uri"
    :sourceId="overviewData?.details?.datasourceId"
    @update="updateHandler")

  AccessDenied( v-if="isAuthorized === false" :associated-domains-names="associatedDomainsNames" )

</template>
